import { useCallback, useState, useRef } from 'react';
import { useInView as inViewHook } from 'react-intersection-observer';

import { isBrowserEnv } from '../env/isBrowserEnv';
import { throttle } from '../performance/throttle';

function useElementVisibleOnScreen() {
  const ref = useRef(null);
  const [isElementVisible, setElementVisibility] = useState(false);

  const handleElementVisibility = throttle(() => {
    const element = ref.current;
    const hasElement = Boolean(element);
    if (hasElement) {
      const isElementVisibleOnScreen = element.getBoundingClientRect().top <= window.innerHeight;
      if (isElementVisibleOnScreen) {
        setElementVisibility(true);

        window.removeEventListener('scroll', handleElementVisibility);
      }
    }
  }, 1000);

  const setRef = useCallback(node => {
    if (node) {
      ref.current = node;

      handleElementVisibility();
      window.addEventListener('scroll', handleElementVisibility, { passive: true });
    }
  }, []);

  return [setRef, isElementVisible];
}

const defaultOptions = {
  isBrowserEnv,
  preferScroll: false,
  intersectionOptions: {
    triggerOnce: true,
    rootMargin: '300px 0px',
  },
};

export const createHook = intersectionHook => options => {
  const config = {
    ...defaultOptions,
    ...options,
  };

  if (!config.isBrowserEnv) {
    return [null, false];
  }

  if (config.preferScroll || typeof window.IntersectionObserver === 'undefined') {
    return useElementVisibleOnScreen();
  }

  return intersectionHook(config.intersectionOptions);
};

const useInView = createHook(inViewHook);

export default useInView;
